import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { LevelOpenFullDoors } from "building-types/doors/levelOpenFullDoors";
import type { LevelOpeningPlacementTable } from "building-types/openings/levelOpeningPlacementTable";
import type { OpeningNoExpr, SolidOpeningPlacement } from "building-types/openings/opening";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { planarGraphAdjacentFace, planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { polyLine2EdgeCount } from "geom/polyLine2";
import { openingPlacementLookup, roomTypeOpeningPlacementTableNew } from "../elements/openings/openingPlacement";
import type { OpeningPlacementFuncOptionsLite } from "../elements/openings/openingPlacementFuncOptions";
import { DefaultRoomTypeOpeningPlacements } from "../elements/openings/openingPlacementTableBuiltIn";
import { makeRoomOpeningPlacementTables } from "../elements/openings/openingPlacementTableByRoomEdge";
import { openingPlacementFuncOptionsLiteInvert } from "../elements/openings/openingUtils";
import { RoomIndexImpl } from "../elements/rooms/roomIndexImpl";
import {
  determineWinningHalfEdge,
  resolveManualOpeningPlacement,
  resolveRoomTypeOpeningPlacement,
} from "../generationTools/generateOpeningPlacementTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

// It's important to use an emptyObject in the select function as it will be
// used as a cache key
const emptyObject = {};

function select({
  levels,
  planarGraphs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  planarGraphPerimeters,
  optionChoices,
  doc: {
    openings,
    optionSets,
    instanceOptionSets,
    entityGroups,
    groups,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    rooms: staticRooms,
  },
  entityDisabledByGroup,
  groupByEntityType,
  relatedRoomsMap,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    levels,
    planarGraphs,
    rooms,
    roomLevelIndices,
    staticRooms,
    roomsByLevel,
    planarGraphPerimeters,
    openings,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    optionChoices: resolveOptionsInFormula(openings, definitions, optionChoices), // Once complete, openings don't have Match Expressions
    rawOptionChoices: optionChoices,
    entityDisabledByGroup,
    openingGroups: groupByEntityType["OPENING"] || emptyObject,
    entityGroups,
    groups,
    relatedRoomsMap,
    definitions,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  optionChoices: "shallow",
  rawOptionChoices: "shallow",
  openings: "deep",
  staticRooms: "shallow",
});

function generate({
  rooms,
  planarGraphs,
  roomLevelIndices,
  roomsByLevel,
  levels,
  planarGraphPerimeters,
  openings,
  roomLayouts,
  roomLayoutInstances,
  staticRooms,
  entityRoomLayouts,
  optionChoices,
  rawOptionChoices,
  definitions,
  entityDisabledByGroup,
  openingGroups,
  groups,
  relatedRoomsMap,
}: ReturnType<typeof select>): {
  openFullDoors: LevelOpenFullDoors;
  solidWallOverrides: Table<SolidOpeningPlacement>;
  openingPlacements: LevelOpeningPlacementTable;
} {
  groups = evalFormulas(groups, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  const openFullDoors: LevelOpenFullDoors = {};
  const openingPlacements: LevelOpeningPlacementTable = {};
  const roomTypeOpeningPlacements = roomTypeOpeningPlacementTableNew(DefaultRoomTypeOpeningPlacements);
  const { manualOpeningPlacementTables, solidWallOverrides } = makeRoomOpeningPlacementTables({
    rooms,
    docRooms: staticRooms,
    openings: openings as Table<OpeningNoExpr>,
    openingGroups,
    groups,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    relatedRoomsMap,
    optionChoices,
    rawOptionChoices,
    entityDisabledByGroup,
    definitions,
  });

  // for each level
  //   for each room
  //     for each edge of the room
  //       determine the room pairs
  //       use the room type pairs to lookup the opening placement algorithm
  //       and if there is one, apply it
  //         - matches by (startRoomType, endRoomType, streetOrientation)
  //         - the first match wins

  // for each level
  for (const levelId in levels) {
    const planarGraph = planarGraphs[levelId];

    const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
    const perimeterStreetOrientationTable = planarGraphPerimeters[levelId].coveredPerimeterStreetOrientations;
    const roomIndex = new RoomIndexImpl(rooms, roomLevelIndices, roomsByLevel, planarGraphs);

    // roomId => [door, precedence, inserted]
    const deferredDoorInsertions: { [roomId: number]: [GeneratedDoorPlacement, number, boolean][] } = {};
    for (const room of levelRooms) {
      deferredDoorInsertions[roomLevelIndices[room.id]] = [];
    }

    const visitedHalfEdges: { [halfEdge: number]: boolean } = {};

    // for each room on level
    for (const room of levelRooms) {
      const manualOpeningPlacements = manualOpeningPlacementTables[room.id];

      const { outline: roomOutline } = room;
      const roomIndexOnLevel = roomLevelIndices[room.id];

      // for each wall of room
      for (let wallIndex = 0, wallCount = polyLine2EdgeCount(roomOutline); wallIndex < wallCount; wallIndex++) {
        const subwalls = planarGraph.polygonToPlanarGraph[roomIndexOnLevel][wallIndex];

        let wallDomainOpeningMatched = false;

        // for each sub-wall of wall
        for (let subWallIndex = 0, subWallCount = subwalls.length; subWallIndex < subWallCount; subWallIndex++) {
          const { index, edgeIndex } = subwalls[subWallIndex];
          const halfEdgeIndex = planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];

          const adjFace = planarGraphAdjacentFace(planarGraph.planarGraph, index, edgeIndex);
          const polygonMap = planarGraph.planarGraphToPolygon[adjFace];
          const adjRoom: Room | undefined =
            polygonMap.length !== 0 && polygonMap[0] ? levelRooms[polygonMap[0].index] : undefined;
          const adjHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);

          if (wallDomainOpeningMatched) {
            // if a wall domain opening has matched, it disqualifies all of the future edge openings on this wall
            visitedHalfEdges[halfEdgeIndex] = true;
            continue;
          }

          if (visitedHalfEdges[halfEdgeIndex]) {
            continue;
          }

          const streetOrientation = perimeterStreetOrientationTable[adjHalfEdgeIndex];
          const adjStreetOrientation = perimeterStreetOrientationTable[halfEdgeIndex];

          let openingPlacement = openingPlacementLookup(
            roomTypeOpeningPlacements,
            manualOpeningPlacements,
            room,
            adjRoom,
            planarGraph.halfEdgeIndexToPolygonEdgeIndex(halfEdgeIndex),
            planarGraph.halfEdgeIndexToPolygonEdgeIndex(adjHalfEdgeIndex),
            streetOrientation,
          );
          let adjOpeningPlacement = openingPlacementLookup(
            roomTypeOpeningPlacements,
            adjRoom ? manualOpeningPlacementTables[adjRoom.id] : undefined,
            adjRoom,
            room,
            planarGraph.halfEdgeIndexToPolygonEdgeIndex(adjHalfEdgeIndex),
            planarGraph.halfEdgeIndexToPolygonEdgeIndex(halfEdgeIndex),
            adjStreetOrientation,
          );

          // if the adjacent opening set is a wall strip domain opening,
          // only room edge openings from this room should affect the room
          if (adjOpeningPlacement?.type === "MANUAL" && adjOpeningPlacement?.exclusive === true) {
            adjOpeningPlacement = undefined;

            if (openingPlacement?.type !== "MANUAL") {
              openingPlacement = undefined;
            }
          } else {
            // if the adjacent opening set is not a wall strip domain opening,
            // then we ensure only one opening "wins"
            visitedHalfEdges[adjHalfEdgeIndex] = true;
          }

          visitedHalfEdges[halfEdgeIndex] = true;

          // decide between original halfEdge or opposite halfEdge or neither
          const halfEdgeWins = determineWinningHalfEdge(openingPlacement, adjOpeningPlacement);

          //if halfEdgeWins is undefined, don't enact a placement
          if (halfEdgeWins === undefined) {
            continue;
          }

          // the lite `OpeningPlacementFuncOptionsLite` are the minimal data required to generate opening placement
          const opts: OpeningPlacementFuncOptionsLite = {
            roomStart: room,
            roomEnd: adjRoom,
            wallIndex,
            halfEdgeIndex,
            planarGraph,
            streetOrientation,
            perimeterStreetOrientationTable,
            roomIndex,
          };

          const selectedPlacement = halfEdgeWins ? openingPlacement : adjOpeningPlacement;

          const selectedPlacementOptions = halfEdgeWins ? opts : openingPlacementFuncOptionsLiteInvert(opts);
          const { roomStart, roomEnd } = selectedPlacementOptions;

          wallDomainOpeningMatched = selectedPlacement?.type === "MANUAL" && !!selectedPlacement?.exclusive;

          assertIsNotUndefined(selectedPlacement);

          switch (selectedPlacement.type) {
            case "ROOM_TYPE": {
              const lookupRoomId = getSourceRoomId(room);
              const staticRoom = staticRooms[lookupRoomId];
              if (!staticRoom || !staticRoom.removeBuiltInOpenings) {
                resolveRoomTypeOpeningPlacement(
                  selectedPlacement,
                  roomStart,
                  roomEnd,
                  openingPlacements,
                  levelId,
                  selectedPlacementOptions,
                  openFullDoors,
                );
              }
              break;
            }
            case "MANUAL": {
              resolveManualOpeningPlacement(
                openingPlacements,
                levelId,
                opts,
                selectedPlacement,
                selectedPlacementOptions,
                openFullDoors,
              );
              break;
            }
          }
        } // sub-walls
      } // walls
    } // rooms
  } // levels

  return { openFullDoors, solidWallOverrides, openingPlacements };
}

export default memo(select, generate, compare);
